import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import type { FileRouteTypes } from '@tanstack/react-router';
import { useAuth } from '../../../../contexts/AuthContext';
import useChart from '../../../../hooks/useChart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import { useIsTerminalScreen } from '../../../../hooks/useIsTerminalScreen';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';
import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import TerminalScreenMessage from '../../../../components/navigation/TerminalScreenMessage';
import { Heading } from '../../../../components/Heading';
import { classNames } from '../../../../style/classNames';
import type { Chart, PatientDoc } from '@usacsmideo/types';
import * as Sentry from '@sentry/browser';

export const Route = createFileRoute('/_authenticated/$patientId/card/share')({
  component: SharePage,
});

function SharePage() {
  const { currentUser } = useAuth();
  const patientId = currentUser?.uid || '';
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const { mutate } = useUpdateUserDoc();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;
  const isTerminal = useIsTerminalScreen(pathname, patientInfo?.navObj);

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo,
      chart,
    });

    if ('incompleteMessage' in res) {
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    if ('complete' in res) {
      mutate(
        { patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/$patientId', params: { patientId } });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }

    mutate(
      { patientId, navObj: res.updatedNavObj },
      {
        onSuccess: () => {
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  if (!patientInfo || !patientInfo.navObj) {
    return <p>Loading...</p>;
  }

  return (
    <div className="flex w-full flex-col items-center">
      <Heading id="share" textContent="Share Your MIDEO Card" />
      <div className="mt-8 flex max-w-lg flex-col items-center text-center">
        <p className="text-mideo-darkred text-4xl font-bold">Coming Soon</p>
        <p className="mt-4 text-xl">
          We are working on a way for you to share your MIDEO card with family members, caregivers, and healthcare
          providers. Stay tuned for updates.
        </p>
      </div>
      {isTerminal ? (
        <TerminalScreenMessage onReturnToOverview={handleNextButtonClick} />
      ) : (
        <button onClick={handleNextButtonClick} className={classNames.nextButton}>
          Next
        </button>
      )}
    </div>
  );
}
